import { useCallback, useEffect, useState } from "react";
import styles from "./ModelsReviewView.module.scss";
import { Button, View, ViewHeader } from "@/components";
import { axiosInstance, getApiErrorCode } from "@/helpers/axios";
import axios from "axios";
import { ForceCatalogApproveDrawer } from "../force-catalog-approve-drawer/ForceCatalogApproveDrawer.comp";
import { formatSpeciesDisplayName } from "../../_constants/content-models-ui.const";

type ReviewVersionRow = {
  id: string;
  species_slug: string;
  species_display_name: string;
  version_label: string;
  variant_key: string;
  status: string;
  change_description: string | null;
  updated_at: number;
  approval_count: number;
  force_self_approve_by?: string | null;
};

function apiMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const m = err.response?.data as { message?: string } | undefined;
    if (m?.message && typeof m.message === "string") return m.message;
  }
  return "Something went wrong.";
}

export const ModelsReviewView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<ReviewVersionRow[]>([]);
  const [requiredApprovals, setRequiredApprovals] = useState(1);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [forceDrawer, setForceDrawer] = useState<{
    id: string;
    titleLine: string;
  } | null>(null);

  const loadQueue = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/v2/models/versions", {
        params: { status: "in_review" },
      });
      if (!data?.success || !Array.isArray(data?.data?.items)) {
        setQueue([]);
        return;
      }
      const d = data.data as { items: ReviewVersionRow[]; requiredApprovals?: number };
      setQueue(d.items);
      setRequiredApprovals(
        typeof d.requiredApprovals === "number" ? d.requiredApprovals : 1
      );
    } catch {
      setError("Could not load review queue.");
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const openInViewer = async (versionId: string) => {
    setOpeningId(versionId);
    try {
      const { data } = await axiosInstance.get(
        `/v2/models/versions/${encodeURIComponent(versionId)}/viewer-token`
      );
      const url = data?.data?.viewerUrl as string | undefined;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setOpeningId(null);
    }
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusyKey(key);
    setFlash(null);
    try {
      await fn();
      await loadQueue();
    } catch (e) {
      setFlash(apiMessage(e));
    } finally {
      setBusyKey(null);
    }
  };

  const approveWithOptionalForceDrawer = async (row: ReviewVersionRow) => {
    const key = `ap-${row.id}`;
    setBusyKey(key);
    setFlash(null);
    try {
      await axiosInstance.post(
        `/v2/models/versions/${encodeURIComponent(row.id)}/approve`
      );
      await loadQueue();
    } catch (e) {
      if (getApiErrorCode(e) === "cannot_self_approve") {
        setForceDrawer({
          id: row.id,
          titleLine: `${formatSpeciesDisplayName(row.species_display_name)} · ${row.version_label}`,
        });
      } else {
        setFlash(apiMessage(e));
      }
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Review queue"
          description="Versions awaiting review appear here. Open a model in the preview app to inspect it before approval."
        />

        {flash ? (
          <p className={styles["comp__flash"]} role="status">
            {flash}
          </p>
        ) : null}

        {loading ? (
          <div className={styles["comp__empty"]}>Loading…</div>
        ) : error ? (
          <div className={styles["comp__empty"]}>{error}</div>
        ) : queue.length === 0 ? (
          <div className={styles["comp__empty"]}>Nothing waiting for review.</div>
        ) : (
          <div className={styles["comp__list"]}>
            {queue.map((row) => {
              const ac =
                typeof row.approval_count === "number" ? row.approval_count : 0;
              const hasForce = Boolean(row.force_self_approve_by);
              const canPublish = ac >= requiredApprovals || hasForce;
              return (
                <article key={row.id} className={styles["comp__card"]}>
                  <div className={styles["comp__card-title"]}>
                    {formatSpeciesDisplayName(row.species_display_name)} ·{" "}
                    {row.version_label}
                    <span className={styles["comp__variant"]}> ({row.variant_key})</span>
                  </div>
                  <p className={styles["comp__meta"]}>
                    Updated {new Date(row.updated_at).toLocaleString()} · Approvals{" "}
                    <strong>
                      {ac}/{requiredApprovals}
                      {hasForce ? " (forced)" : ""}
                    </strong>
                  </p>
                  {row.change_description ? (
                    <div className={styles["comp__notes"]}>
                      <span className={styles["comp__notes-label"]}>What changed</span>
                      <p>{row.change_description}</p>
                    </div>
                  ) : null}
                  <div className={styles["comp__actions"]}>
                    <Button
                      size="small"
                      variant="secondary"
                      disabled={openingId === row.id || busyKey !== null}
                      onClick={() => void openInViewer(row.id)}
                    >
                      {openingId === row.id ? "Opening…" : "Open model"}
                    </Button>
                    <Button
                      size="small"
                      variant="secondary"
                      disabled={busyKey !== null}
                      onClick={() => void approveWithOptionalForceDrawer(row)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="primary"
                      disabled={!canPublish || busyKey !== null}
                      onClick={() =>
                        void run(`pub-${row.id}`, async () => {
                          await axiosInstance.post(
                            `/v2/models/versions/${encodeURIComponent(row.id)}/activate`
                          );
                        })
                      }
                    >
                      Publish
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <ForceCatalogApproveDrawer
          isOpen={forceDrawer !== null}
          onClose={() => setForceDrawer(null)}
          versionId={forceDrawer?.id ?? null}
          titleLine={forceDrawer?.titleLine ?? ""}
          onSuccess={() => void loadQueue()}
        />
      </div>
    </View>
  );
};
